import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
  useMediaQuery,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  IconButton,
  Skeleton,
  Avatar,
  Tooltip as MuiTooltip,
  Chip,
} from "@mui/material";
import { tokens } from "../../../theme";

import {
  InfoOutlined,
  BarChart as BarChartIcon,
  TableChart,
  Download,
  Paid,
  ErrorOutline,
} from "@mui/icons-material";
import axios from "axios";

const TopCustomersWithHighestDues = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const token = localStorage.getItem("token");
  const cmpid = localStorage.getItem("cmpid");

  // State management
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("chart");
  const [usingDummyData, setUsingDummyData] = useState(false);

  // Color configuration
  const CONFIG = useMemo(
    () => ({
      colors: {
        primary: [
          "#1976d2", // Blue
          "#4caf50", // Green
          "#f44336", // Red
          "#ff9800", // Orange
          "#9c27b0", // Purple
          "#009688", // Teal
          "#e91e63", // Pink
          "#3f51b5", // Indigo
          "#ffeb3b", // Yellow
          "#795548", // Brown
        ],
        cardBg: theme.palette.background.default,
        textPrimary: theme.palette.text.primary,
        textSecondary: theme.palette.text.secondary,
        success: "#4caf50",
        warning: "#f44336",
      },
    }),
    [theme]
  );

  // Generate dummy data
  const generateDummyData = useCallback(() => {
    const companies = [
      "Tech Solutions Inc.",
      "Global Retail Corp.",
      "Innovative Designs LLC",
      "Premium Services Co.",
      "Elite Manufacturing",
      "Quality Products Ltd.",
      "Swift Distribution",
      "Advanced Systems",
      "Creative Marketing",
      "Reliable Logistics",
    ];

    return companies
      .map((company, index) => {
        const amount = Math.floor(Math.random() * 500000) + 100000;

        return {
          id: index + 1,
          name: company,
          amount,
          days: Math.floor(Math.random() * 90) + 10, // random days
        };
      })
      .sort((a, b) => b.amount - a.amount);
  }, []);

  // Calculate statistics
  const { totalReceivables, averageDue } = useMemo(() => {
    if (data.length === 0) return { totalReceivables: 0, averageDue: 0 };

    const total = data.reduce((sum, item) => sum + item.amount, 0);
    const avg = total / data.length;

    return {
      totalReceivables: total,
      averageDue: avg,
    };
  }, [data]);

  // Load data (API + fallback dummy)
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUsingDummyData(false);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/reports/Top10HighestcustomerDues`,
        { cmpid },
        {
          headers: { Authorization: token },
        }
      );

      if (response.data && Object.keys(response.data).length > 0) {
        const apiData = Object.values(response.data)
          .map((item, index) => ({
            id: index + 1,
            name: item.PartyName || `Customer ${index + 1}`,
            amount: Number(item.outstanding_amount) || 0,
          }))
          .sort((a, b) => b.amount - a.amount);

        setData(apiData);
      } else {
        // API returned empty data, use dummy data
        setData(generateDummyData());
        setUsingDummyData(true);
        setError("No data available. Showing sample data.");
      }
    } catch (err) {
      console.error("API Error:", err);
      setError("Failed to load API data. Showing sample data.");
      setData(generateDummyData());
      setUsingDummyData(true);
    } finally {
      setLoading(false);
    }
  }, [cmpid, token, generateDummyData]);

  // Toggle view
  const toggleViewMode = useCallback(() => {
    setViewMode(viewMode === "chart" ? "table" : "chart");
  }, [viewMode]);

  // Export data
  const exportToCSV = useCallback(() => {
    const headers = ["Sr No", "Customer Name", "Outstanding Amount (₹)"];
    const rows = data.map((item) => [item.id, item.name, item.amount]);

    let csvContent =
      "data:text/csv;charset=utf-8," +
      headers.join(",") +
      "\n" +
      rows.map((row) => row.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "top_customers_with_dues.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [data]);

  // Custom tooltip
  const CustomTooltip = useCallback(
    ({ active, payload }) => {
      if (active && payload && payload.length) {
        const dataItem = payload[0].payload;

        return (
          <Card
            sx={{
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[3],
              borderRadius: 2,
              p: 2,
              minWidth: 180,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {dataItem.name}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Stack spacing={1}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Outstanding Amount:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  ₹{dataItem.amount.toLocaleString()}
                </Typography>
              </Box>
            </Stack>
          </Card>
        );
      }
      return null;
    },
    [theme]
  );

  // On mount load data
  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Card
      sx={{
        height: "100%",
        minHeight: 650,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[2],
        borderRadius: "12px",
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: theme.shadows[6],
        },
      }}
    >
      <CardContent
        sx={{
          p: isMobile ? 2 : 3,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Header Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: CONFIG.colors.textPrimary,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Paid
                sx={{
                  color: CONFIG.colors.primary[0],
                  fontSize: 28,
                }}
              />
              Top 10 Customers with Highest Dues
              {usingDummyData && (
                <MuiTooltip title="Customers with the highest outstanding receivables">
                  <InfoOutlined
                    sx={{
                      fontSize: 18,
                      color: CONFIG.colors.textSecondary,
                      cursor: "pointer",
                    }}
                  />
                </MuiTooltip>
              )}
              {!usingDummyData && (
                <MuiTooltip title="Customers with the highest outstanding receivables">
                  <IconButton sx={{ p: 0 }}>
                    <InfoOutlined
                      sx={{
                        fontSize: 18,
                        color: CONFIG.colors.textSecondary,
                        cursor: "pointer",
                      }}
                    />
                  </IconButton>
                </MuiTooltip>
              )}
            </Typography>
            {(error || usingDummyData) && (
              <Box>
                <Chip
                  label={error ? error : "No data available. Showing sample data."}
                  color="warning"
                  size="small"
                  variant="outlined"
                  icon={<ErrorOutline fontSize="small" />}
                  sx={{
                    mb: 1,
                    borderColor: colors.warning,
                    color: colors.warning,
                  }}
                />
                {usingDummyData && (
                  <Typography variant="caption" color="text.secondary">
                    Sample data may not reflect actual business metrics
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Button
              size="small"
              onClick={toggleViewMode}
              startIcon={
                viewMode === "chart" ? <TableChart /> : <BarChartIcon />
              }
              sx={{
                textTransform: "none",
                color: CONFIG.colors.textSecondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              {viewMode === "chart" ? "Table" : "Chart"}
            </Button>

            <Button
              size="small"
              onClick={exportToCSV}
              startIcon={<Download />}
              sx={{
                textTransform: "none",
                color: CONFIG.colors.textSecondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Stats Summary */}
        <Box display="flex" flexDirection="column" gap={2}>
          {loading ? (
            <Skeleton variant="rounded" width="100%" height={120} />
          ) : (
            <Box
              display="grid"
              gridTemplateColumns={isMobile ? "1fr" : "repeat(2, 1fr)"}
              gap={2}
            >
              {/* Total Receivables */}
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: CONFIG.colors.cardBg,
                  border: `1px solid ${theme.palette.divider}`,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total Outstanding
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5}>
                  ₹{totalReceivables.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Across top 10 customers
                </Typography>
              </Paper>

              {/* Average Due */}
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: CONFIG.colors.cardBg,
                  border: `1px solid ${theme.palette.divider}`,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Average Outstanding
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5}>
                  ₹{Math.round(averageDue).toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Per customer
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>

        {/* Chart/Table */}
        <Box flex={1} minHeight={300} position="relative" mt={2}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
            >
              <CircularProgress size={isMobile ? 40 : 50} />
            </Box>
          ) : viewMode === "chart" ? (
            <Box
              height="100%"
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
              p={2}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.action.hover} 100%)`,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data}
                  layout="vertical"
                  margin={{
                    top: 20,
                    right: 30,
                    left: 40,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={theme.palette.divider}
                  />
                  <XAxis
                    type="number"
                    tick={{ fill: theme.palette.text.primary }}
                    axisLine={{ stroke: theme.palette.divider }}
                    label={{
                      position: "insideBottom",
                      offset: -5,
                      fill: theme.palette.text.primary,
                    }}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={isMobile ? 80 : 120}
                    tick={{
                      fill: theme.palette.text.primary,
                      fontSize: isMobile ? 12 : 14,
                    }}
                    axisLine={{ stroke: theme.palette.divider }}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
                  />
                  <Legend />
                  <Bar
                    dataKey={"amount"}
                    name={"Outstanding Amount"}
                    barSize={isMobile ? 20 : 30}
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          CONFIG.colors.primary[
                            index % CONFIG.colors.primary.length
                          ]
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 400,
                "& .MuiTableCell-root": {
                  py: 1.5,
                },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>Customer Name</TableCell>
                    <TableCell align="right">Outstanding Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, index) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor:
                                CONFIG.colors.primary[
                                  row.id % CONFIG.colors.primary.length
                                ],
                              fontSize: 14,
                            }}
                          >
                            {row.name.charAt(0) || "C"}
                          </Avatar>
                          <Typography variant="body2" noWrap>
                            {row.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={500}>
                          ₹{row.amount.toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default TopCustomersWithHighestDues;